import { useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useAdminTheme } from '../lib/AdminTheme';
import AdminThemeToggle from './AdminThemeToggle';
import { Package, Tag, ArrowLeft, Briefcase, ShoppingBag, Settings, Activity, Wallet } from 'lucide-react';

const NAV = [
  { to: '/admin/products', label: 'Producten', icon: Package },
  { to: '/admin/categories', label: 'Categorieen', icon: Tag },
  { to: '/admin/orders', label: 'Bestellingen', icon: ShoppingBag },
  { to: '/admin/b2b', label: 'B2B Aanvragen', icon: Briefcase },
  { to: '/admin/credits', label: 'B2B Credits', icon: Wallet },
  { to: '/admin/config', label: 'Configuratie', icon: Settings },
];

export default function AdminLayout() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { preference, effectiveTheme, setPreference } = useAdminTheme();

  useEffect(() => {
    if (isLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') navigate('/');
  }, [user, isLoading, navigate]);

  if (isLoading || !user || user.role !== 'admin') return null;

  return (
    // `dark` class is scoped to this layout root ONLY. Storefront,
    // checkout and account pages never see it.
    <div
      data-testid="admin-layout"
      data-theme={effectiveTheme}
      className={`${effectiveTheme === 'dark' ? 'dark' : ''} flex min-h-[calc(100vh-64px)] bg-background text-foreground`}
    >
      <aside className="w-56 shrink-0 bg-sidebar border-r border-sidebar-border flex flex-col">
        <div className="px-5 py-5 border-b border-sidebar-border">
          <p className="font-serif text-sidebar-foreground text-lg font-medium">Admin</p>
          <p className="text-xs text-muted-foreground mt-0.5">The Herborist</p>
          <div data-testid="payment-mode-badge" className="mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            Testmodus
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`admin-nav-${to.split('/').pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-sidebar-accent hover:text-foreground'
                }`
              }
            >
              <Icon className="w-4 h-4 shrink-0" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-sidebar-border space-y-2">
          <AdminThemeToggle
            preference={preference}
            effectiveTheme={effectiveTheme}
            onChange={setPreference}
            variant="admin"
          />
          <NavLink
            to="/ops"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors"
          >
            <Activity className="w-4 h-4" /> Ops Center
          </NavLink>
          <NavLink
            to="/catalog"
            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Terug naar winkel
          </NavLink>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-background text-foreground">
        <Outlet />
      </main>
    </div>
  );
}
